import {Component, EventEmitter, Input, OnInit, Output} from '@angular/core';
import {
  Packet,
  Poi,
  Route,
  RouteLoader,
  RouteParams,
  Tracker,
  TransportInterventionPoi,
  transportInterventionPoiDiffAndGroupReport, TransportInterventionPoiGroupReport,
  TransportInterventionsStatus
} from 'shared';
import {featureGroup, FeatureGroup, Map, Marker, tileLayer} from 'leaflet';
import {ReplaySubject, Subscription} from 'rxjs';
import * as L from 'leaflet';
import {TractorService} from '../../stores/tractor.service';
import moment from 'moment';
import {WebsocketService} from '../../services/websocket/websocket.service';
import {delay, filter, first} from 'rxjs/operators';
import {TransportInterventionService} from '../../stores/transport-intervention.service';

@Component({
  selector: 'app-transport-interventions-realtime-report',
  templateUrl: './transport-interventions-realtime-report.component.html',
  styleUrls: ['./transport-interventions-realtime-report.component.scss']
})
export class TransportInterventionsRealtimeReportComponent implements OnInit {

  @Input()
  status: TransportInterventionsStatus;

  @Input()
  visible: boolean;

  @Output()
  visibleChange = new EventEmitter<boolean>();

  title: string;

  private map: Map;
  private _mapSubject = new ReplaySubject<Map>(1);
  mapReady$ = this._mapSubject.asObservable();

  route: Route;
  routeParams: RouteParams;
  routeLoader: RouteLoader = new RouteLoader();
  isRouteLoading: boolean;
  marker: Marker;
  routeLayer: FeatureGroup = featureGroup();
  poiMapLayer: FeatureGroup = featureGroup();
  lastPoi?: Poi;
  poiListGroupReport: TransportInterventionPoiGroupReport[];

  layers = [
    {
      name: 'Hybride',
      layer: tileLayer('https://{s}.google.com/vt/lyrs=m&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
        maxZoom: 21,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      }),
    },
    {
      name: 'Satellite',
      layer: tileLayer('https://{s}.google.com/vt/lyrs=s,h&x={x}&y={y}&z={z}&hl=ar-MA&gl=MA', {
        maxZoom: 21,
        subdomains: ['mt0', 'mt1', 'mt2', 'mt3']
      }),
    },
  ];

  mapOptions = {
    layers: this.layers.map(l => l.layer),
    zoom: 7,
    minZoom: 0,
    maxZoom: 21,
    maxNativeZoom: 21,
    preferCanvas: false,
    center: [33.626756199999996, -7.4964748]
  };

  private trackingSub: Subscription;
  private statusSub: Subscription;

  constructor(
    private tractorService: TractorService,
    private ws: WebsocketService,
    private transportInterventionService: TransportInterventionService,
  ) {
  }

  ngOnInit(): void {
  }

  onMapReady(map: Map) {
    this.map = map;
    this._mapSubject.next(map);
    L.control.scale({imperial: false}).addTo(map);
    map.addLayer(this.routeLayer);
    map.addLayer(this.poiMapLayer);
  }

  startFollow(status: TransportInterventionsStatus) {
    this.visible = true;
    this.visibleChange.emit(true);
    this.status = status;
    this.title = 'Suivi ' + status.vehicle_name;

    // Marker
    const icon = L.icon({
      iconUrl: '/assets/icons/arrow_orange.png',
      iconSize: [16, 24],
      iconAnchor: [8, 24]
    });

    this.marker = new Marker([status.tracker_lat, status.tracker_lng], {
      icon,
      rotationAngle: status.tracker_angle,
      rotationOrigin: 'center center',
    });

    this.mapReady$.pipe(first()).subscribe(() => {
      this.marker.addTo(this.map);
    });

    // Route
    this.isRouteLoading = true;
    this.routeParams = new RouteParams(1);
    this.tractorService.route(this.routeLoader,
      this.status.vehicle_id,
      this.status.start_time,
      moment().toDate(),
      this.routeParams,
    ).subscribe((r) => {
      if (r.isLoaded) {
        this.isRouteLoading = false;
        this.route = r.isEmpty() ? new Route({data: []}) : r.route;
        this.updateRouteLayer();
      }
    });

    // Tracking events
    this.trackingSub = this.ws.topicObserver('Tracker', Tracker).pipe(
      filter((t: Tracker) => t.imei === status.tracker_imei),
    ).subscribe((t: Tracker) => {
      this.marker.setLatLng([t.lat, t.lng]);

      // Update icon
      this.marker.setIcon(L.icon({
        iconUrl: t.stateArrowIcon(),
        iconSize: [16, 24],
        iconAnchor: [8, 24]
      }));

      // Update angle
      this.marker.setRotationAngle(t.angle);

      if (this.route) {
        // Update route
        this.route.data.push(new Packet({
          server_time: t.server_time,
          tracker_time: t.tracker_time,
          lat: t.lat,
          lng: t.lng,
          speed: t.speed,
          altitude: t.altitude,
          angle: t.angle,
        }));
        this.updateRouteLayer();
      }
    });


    // Interventions status
    this.statusSub = this.ws.topicObserver('TransportInterventionsStatus', TransportInterventionsStatus)
      .pipe(
        filter((s: TransportInterventionsStatus) => s.id === status.id),
      ).subscribe((s: TransportInterventionsStatus) => {
        this.status = s;

        // POIs
        if (s.realtime.current_poi) {
          if ((this.lastPoi && this.lastPoi.id !== s.realtime.current_poi.id) || !this.lastPoi) {
            this.poiMapLayer.clearLayers();
            this.transportInterventionService.poiList(s.id).subscribe(list => {
              this.poiListGroupReport = transportInterventionPoiDiffAndGroupReport(list.data as TransportInterventionPoi[]);
              this.addPoiListToMap();
            });
          }
          this.lastPoi = s.realtime.current_poi;
        } else {
          this.lastPoi = null;
        }
      });
  }

  stopFollow() {
    this.marker.remove();
    this.routeLayer.clearLayers();
    this.poiMapLayer.clearLayers();
    this.trackingSub.unsubscribe();
    this.statusSub.unsubscribe();
  }

  closed(e: any) {
    this.visibleChange.emit(false);
    this.stopFollow();
  }

  private updateRouteLayer() {
    this.routeLayer.clearLayers();

    if (this.route.data.length === 0) {
      return;
    }

    const routePathLayer = this.route.layer({
      color: '#007bff',
      weight: 2,
      opacity: 1
    });
    this.routeLayer.addLayer(routePathLayer);

    this.mapReady$.pipe(first()).subscribe(() => {
      this.map.fitBounds(routePathLayer.getBounds(), {
        padding: [20, 20],
        animate: true,
      });
    });
  }

  private addPoiListToMap() {
    this.poiListGroupReport.forEach((r) => {
      const poi = r.poi;
      const layer = poi.layer;

      layer.bindPopup(() => {
        const popupEl = document.createElement('transport-intervention-map-poi-report-popup') as any;
        popupEl.report = r;
        return popupEl;
      }, {
        offset: [0, -20],
        minWidth: 400,
        maxWidth: 400,
        className: 'transport-intervention-map-poi-report-popup',
      });

      layer.bindTooltip(poi.name + r.visits_numbers, {
        direction: poi.type === 'POLYGON' ? 'center' : 'top',
        offset: [0, -40],
        opacity: 0.8,
      });

      this.poiMapLayer.addLayer(layer);
    });
    this.map.fitBounds(this.poiMapLayer.getBounds());
  }
}
